import { Col, Form, Input, Row, Select, Typography } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import { FormattedMessage, useIntl } from 'react-intl';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import {
  WorkOrderComplementTankLevel,
  WorkOrderComplementType,
  workorderComplementColor,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { CustomerType } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import InsurerInfiniteSelectField from '@/components/_common/InsurerInfiniteSelectField';
import { Moment } from 'moment';
import { useDefaultEmptyFieldError } from '@/hooks';
import StatusTag from '@/components/_common/StatusTag';
import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import { ComplementReplicateFormData } from '../ReplicateComplementModal';
import { isAppPortocar } from '@/index';

interface Props {
  form: FormInstance<ComplementReplicateFormData>;
}

const ReplicateComplementFormView: React.FC<Props> = ({ form }) => {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const { Title } = Typography;

  const {
    complement: { data: complement },
    company: { companyConfig: configBudget },
  } = useWorkorderDetailContext();

  const tagColor = complement?.status
    ? workorderComplementColor[complement.status]
    : 'red';

  return (
    <>
      <Row
        gutter={[16, 8]}
        align="middle"
        justify="space-between"
        style={{ marginBottom: '24px' }}
      >
        <Col>
          <Typography.Title level={4} id="status">
            <FormattedMessage id="commons.entity.situation" />:
            <StatusTag
              style={{ marginLeft: 5 }}
              size="lg"
              color={tagColor}
              label={intl.formatMessage({
                id: `workOrders.domain.enums.work_order_status.${
                  complement?.status.split('.').slice(-1)[0]
                }`,
              })}
            />
          </Typography.Title>
        </Col>
      </Row>

      <Row gutter={[16, 8]}>
        <Col xs={24} sm={24} md={6}>
          <FloatingFormItem
            name={['complement', 'companyId']}
            label={intl.formatMessage({
              id: 'workOrders.domain.company',
            })}
          >
            <CompaniesInfiniteSelectField disabled />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={6}>
          <FloatingFormItem
            name={['complement', 'estimatorId']}
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.estimator',
            })}
          >
            <EmployeesInfiniteSelectField
              queryFilter={{ estimator: true }}
              disabled
              allowClear
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={6}>
          <FloatingFormItem
            name={['complement', 'type']}
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.type',
            })}
          >
            <Select
              disabled
              allowClear
              style={{ width: '100%' }}
              options={Object.values(WorkOrderComplementType).map((type) => ({
                label: intl.formatMessage({
                  id: `workOrders.${
                    isAppPortocar && type === WorkOrderComplementType.Courtesy
                      ? 'domain.enums.work_order_type.insideCourtesy'
                      : type
                  }`,
                }),
                value: type,
              }))}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={6}>
          <FloatingFormItem
            name={['complement', 'customerType']}
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.customerType',
            })}
            getValueFromEvent={(type) => {
              form.resetFields([
                'insurerId',
                'accidentNumber',
                'insurerDeductibleAmount',
              ]);
              return type;
            }}
          >
            <Select
              disabled
              allowClear
              style={{ width: '100%' }}
              options={Object.values(CustomerType).map((a) => ({
                label: intl.formatMessage({ id: `workOrders.${a}` }),
                value: a,
              }))}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev.customerType !== curr.customerType
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const customerType = getFieldValue([
                'complement',
                'customerType',
              ]);

              return (
                customerType === CustomerType.Insurance && (
                  <Row gutter={[16, 8]}>
                    <Col span={24}>
                      <Title level={5}>
                        <FormattedMessage id="workOrders.domain.complements.insurer" />
                      </Title>
                    </Col>

                    <Col span={24} md={8}>
                      <FloatingFormItem
                        name={['complement', 'insurerId']}
                        label={intl.formatMessage({
                          id: 'workOrders.domain.complements.insurer',
                        })}
                      >
                        <InsurerInfiniteSelectField disabled />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={8}>
                      <FloatingFormItem
                        name={['complement', 'accidentNumber']}
                        label={intl.formatMessage({
                          id: 'workOrders.domain.complements.accidentNumber',
                        })}
                      >
                        <Input disabled />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={8}>
                      <FloatingFormItem
                        name={['complement', 'insurerDeductibleAmount']}
                        initialValue={0}
                        label={intl.formatMessage({
                          id: 'workOrders.domain.complements.insurerDeductibleAmount',
                        })}
                      >
                        <InputDecimal disabled />
                      </FloatingFormItem>
                    </Col>
                  </Row>
                )
              );
            }}
          </Form.Item>
        </Col>

        <Col span={24} style={{ width: '100%' }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={24} md={8} lg={4}>
              <FloatingFormItem
                name={['complement', 'entryDate']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.entryDate',
                })}
              >
                <InputDate disabled />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={8} lg={5}>
              <FloatingFormItem
                name={['complement', 'authorizationDate']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.authorizationDate',
                })}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value: Moment) {
                      if (
                        !value ||
                        value.isSameOrAfter(
                          getFieldValue([
                            'complement',
                            'entryDate',
                          ]).milliseconds(0),
                          'date',
                        )
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error('Deve ser maior que a data de entrada'),
                      );
                    },
                  }),
                  defaultEmptyFieldError,
                ]}
              >
                <InputDate showTime />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={8} lg={5}>
              <FloatingFormItem
                name={['complement', 'closingDate']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.closingDate',
                })}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value: Moment) {
                      if (
                        !value ||
                        value.isSameOrAfter(
                          getFieldValue([
                            'complement',
                            'authorizationDate',
                          ]).milliseconds(0),
                          'date',
                        )
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error('Deve ser maior que a data de autorização'),
                      );
                    },
                  }),
                ]}
              >
                <InputDate disabled showTime />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={8} lg={5}>
              <FloatingFormItem
                name={['complement', 'estimatedLeaveDate']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.estimatedLeaveDate',
                })}
              >
                <InputDate showTime />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={8} lg={5}>
              <FloatingFormItem
                name={['complement', 'returnDate']}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.returnDate',
                })}
              >
                <InputDate showTime />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <FloatingFormItem
            name={['complement', 'prisma']}
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.prisma',
            })}
            rules={[
              {
                required: configBudget?.prisma,
                message: intl.formatMessage({
                  id: 'workOrders.fieldRequiredByConfigCompany',
                }),
              },
            ]}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <FloatingFormItem
            name={['complement', 'currentKilometers']}
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.currentKilometers',
            })}
            rules={[
              {
                required: configBudget?.isKmRequired,
                message: intl.formatMessage({
                  id: 'workOrders.fieldRequiredByConfigCompany',
                }),
              },
            ]}
          >
            <InputDecimal min={0} fractions={0} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <FloatingFormItem
            name={['complement', 'tankLevel']}
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.tankLevel',
            })}
            rules={[
              {
                required: configBudget?.tankLevel,
                message: intl.formatMessage({
                  id: 'workOrders.fieldRequiredByConfigCompany',
                }),
              },
            ]}
          >
            <Select
              allowClear
              style={{ width: '100%' }}
              options={Object.values(WorkOrderComplementTankLevel).map((a) => ({
                label: intl.formatMessage({ id: `workOrders.${a}` }),
                value: a,
              }))}
            />
          </FloatingFormItem>
        </Col>
      </Row>
    </>
  );
};

export default ReplicateComplementFormView;
